import React, { useState, useCallback, useMemo } from "react";
import {
  Lbl,
  Spacer,
} from "../../../../components";
import { FC } from "react";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { ProjectProductMeasurement } from "../../../../redux/types/Material";
import { CheckCircle, RadioButtonUnchecked, ArrowForward } from "@mui/icons-material";
import { MaterialShop } from "./MaterialShop";

interface MaterialSelectionProps {
  handleRefetchProjectDetails: Function;
  projectData?: CustomerProjectDetails;
}

export const MaterialSelection: FC<MaterialSelectionProps> = ({
  handleRefetchProjectDetails,
  projectData,
}) => {
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProjectProductMeasurement | null>(null);

  // Get material measurements from project data and filter by isMaterialSelectionRequired flag
  const materialMeasurements: ProjectProductMeasurement[] = useMemo(() => {
    const allMaterialMeasurements = projectData?.insuranceScope?.materialMeasurements || [];
    const productSystems = projectData?.productSystem || [];

    if (!Array.isArray(allMaterialMeasurements) || allMaterialMeasurements.length === 0) {
      return [];
    }

    // Return only material measurements where productSystem has isMaterialSelectionRequired: true
    return allMaterialMeasurements.filter((measurement) => {
      const productSystem = productSystems.find((ps: any) => ps.id === measurement.productId);
      return productSystem?.isMaterialSelectionRequired !== false;
    });
  }, [projectData]);

  const handleMaterialClick = useCallback((materialId: string) => {
    // Here you would typically navigate to a detailed material selection page
    // For now, we'll just toggle selection
    setSelectedMaterials(prev => 
      prev.includes(materialId) 
        ? prev.filter(id => id !== materialId)
        : [...prev, materialId]
    );
  }, []);

  const handleMaterialSelection = useCallback((material: ProjectProductMeasurement) => {
    // Navigate to the material shop for this product
    setSelectedProduct(material);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedProduct(null);
  }, []);

  // Check if material is completed (uses isSelected at materialMeasurement level)
  const isMaterialCompleted = useCallback((material: ProjectProductMeasurement) => {
    return material.isSelected === true;
  }, []);

  // If a product is selected, show the MaterialShop
  if (selectedProduct) {
    // Get the fresh product data from projectData to ensure we have the latest material selections
    const freshSelectedProduct = materialMeasurements.find(
      material => material.productId === selectedProduct.productId
    ) || selectedProduct;
    
    return (
      <MaterialShop
        handleRefetchProjectDetails={handleRefetchProjectDetails}
        projectData={projectData}
        selectedProduct={freshSelectedProduct}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div
      style={{
        padding: "16px",
        backgroundColor: "#f8fafc",
        minHeight: "100%",
        width: "100%",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "8px",
            flexWrap: "wrap",
          }}
        >
          <h1
            style={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#1f2937",
              margin: 0,
            }}
          >
            Material Selection
          </h1>
        </div>
        <p
          style={{
            fontSize: "14px",
            color: "#6b7280",
            margin: 0,
            lineHeight: "1.5",
          }}
        >
          Please select materials for each product in your project. These are
          based on measurements from your project manager.
        </p>
      </div>

      {/* Materials List */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          width: "100%",
        }}
      >
        {Array.isArray(materialMeasurements) && materialMeasurements.length > 0 ? (
          materialMeasurements.map((material, index) => {
            const isCompleted = isMaterialCompleted(material);
            return (
            <div
              key={material.productId || index}
              onClick={() => handleMaterialSelection(material)}
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "16px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                flexWrap: "wrap",
                gap: "12px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flex: 1,
                }}
              >
                {/* Material Name */}
                <div
                  style={{
                    flex: 1,
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "#1f2937",
                      textTransform: "capitalize",
                    }}
                  >
                    {material.name?.charAt(0).toUpperCase() + 
                     material.name?.slice(1).toLowerCase()}
                  </h3>
                  {material.measurements && material.measurements.length > 0 && (
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "12px",
                        color: "#6b7280",
                      }}
                    >
                      {material.measurements.length} measurement{material.measurements.length > 1 ? 's' : ''} available
                    </p>
                  )}
                </div>

                {/* Status Badge */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "500",
                    backgroundColor: isCompleted 
                      ? "rgba(194, 217, 255, 1)" 
                      : "#fff",
                    border: isCompleted 
                      ? "none" 
                      : "2px solid rgba(194, 217, 255, 1)",
                  }}
                >
                  {isCompleted ? (
                    <>
                      <CheckCircle
                        style={{
                          width: 16,
                          height: 16,
                          color: "#059669",
                        }}
                      />
                      <span style={{ color: "#059669" }}>Completed</span>
                    </>
                  ) : (
                    <>
                      <RadioButtonUnchecked
                        style={{
                          width: 16,
                          height: 16,
                          color: "#f59e0b",
                        }}
                      />
                      <span style={{ color: "#f59e0b" }}>In Progress</span>
                    </>
                  )}
                </div>
              </div>

              {/* Arrow Icon */}
              <ArrowForward
                style={{
                  width: 20,
                  height: 20,
                  color: "#6b7280",
                  marginLeft: "16px",
                }}
              />
            </div>
          )})
        ) : (
          <div
            style={{
              backgroundColor: "#ffffff",
              border: "2px dashed #d1d5db",
              borderRadius: "12px",
              padding: "48px 24px",
              textAlign: "center",
              color: "#6b7280",
            }}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                backgroundColor: "#f3f4f6",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px auto",
              }}
            >
              <RadioButtonUnchecked
                style={{
                  width: 32,
                  height: 32,
                  color: "#9ca3af",
                }}
              />
            </div>
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "16px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              No Materials Available
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              No material measurements have been added to this project yet.
              Please contact your project manager for more information.
            </p>
          </div>
        )}
      </div>

      {/* Support Section */}
      {materialMeasurements.length > 0 && (
        <div
          style={{
            marginTop: "40px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 16px",
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              fontSize: "14px",
              color: "#6b7280",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f9fafb";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#ffffff";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
          >
            <img
              src="https://imagedelivery.net/ljlAKBP_91m1yO7rrhjKgQ/3460b5f5-0adf-4ecd-b334-288411dfe400/chatImage"
              alt="Support"
              style={{
                width: 16,
                height: 16,
              }}
            />
            <span>Missing something? Contact your support specialist.</span>
          </div>
        </div>
      )}

      <Spacer size={3} direction="col" />
    </div>
  );
};
